import { ReactNode } from "react";
import { FieldErrors } from "react-hook-form";
import { mdiFileDocumentEdit, mdiLoading, mdiLock } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";
import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Tooltip from "components/Tooltip";
import { showErrorMessage } from "helpers/toastMessage";
import DocumentReviewRequirementModal from "./DocumentReviewRequirementModal";
import { useFundingRequestForm } from "./FundingRequestFormContext";
import { getDocumentReviewTooltipMessage } from "./documentReviewEligibility";
import { IEditFundingRequest } from "../../EditFundingRequest/StepOne";

interface IProps {
  tooltipPosition?: "up" | "down";
  tooltipContainerClassName?: string;
}

export default function CarrierActions({
  tooltipPosition = "down",
  tooltipContainerClassName = "flex-1"
}: Readonly<IProps>) {
  const {
    methods,
    attachments,
    submitFR,
    refetch,
    isSubmitting,
    isGlobalDisabled,
    canSubmitToDocumentReview,
    isFuelAdvance,
    submitToDocumentReview,
    isSubmittingToDocumentReview,
    isMarkedCompleteState
  } = useFundingRequestForm();
  const [isSubmitConfirmOpen, toggleSubmitConfirmModal] = useToggle();
  const [isDocsRequiredOpen, toggleDocsRequiredModal] = useToggle();

  const isBusy = isSubmitting || isSubmittingToDocumentReview;

  const handleSave = async (): Promise<void> => {
    await methods.handleSubmit(
      async (data) => {
        try {
          const didSave = await submitFR(data);
          if (didSave) {
            await refetch();
          }
        } catch (error) {
          if (error !== "cancel-edit-fr") {
            showErrorMessage("Failed to update the funding request. Please try again.");
          }
        }
      },
      (_errors: FieldErrors<IEditFundingRequest>) => {
        showErrorMessage("Please fix the form errors before updating.");
      }
    )();
  };

  const saveLabel = (): ReactNode => {
    if (isSubmitting) return <Icon path={mdiLoading} size={1} spin={1} />;
    return "Save Updates";
  };

  const submitLabel = (): ReactNode => {
    if (isSubmittingToDocumentReview) return <Icon path={mdiLoading} size={1} spin={1} />;
    return isMarkedCompleteState ? "Submitted to Doc Review" : "Submit to Doc Review";
  };

  return (
    <>
      {/* Stacked in the narrow desktop rail, side by side on mobile where the sticky footer is wide */}
      <div className="grid gap-2 mobile:grid-cols-2">
        <Button
          type="button"
          variant="outline"
          className="w-full hover:bg-[rgb(var(--primary))] hover:text-white flex items-center justify-center whitespace-nowrap mobile:px-2 mobile:text-xs"
          onClick={() => void handleSave()}
          disabled={isGlobalDisabled || isBusy}>
          <Icon
            path={mdiFileDocumentEdit}
            size={1}
            className="mr-5 mobile:mr-1 shrink-0 mobile:!w-5 mobile:!h-5"
          />
          {saveLabel()}
        </Button>

        <Tooltip
          message={getDocumentReviewTooltipMessage(attachments, isFuelAdvance)}
          position={tooltipPosition}
          tooltipContainerClassName={tooltipContainerClassName}
          disabled={canSubmitToDocumentReview || isMarkedCompleteState}>
          <Button
            type="button"
            variant="contained"
            className={clsx(
              "w-full !bg-red-600 hover:!bg-red-700 !text-white font-semibold",
              "flex items-center justify-center whitespace-nowrap mobile:px-2 mobile:text-xs"
            )}
            // Clickable with documents outstanding on purpose: a disabled button explains nothing
            // to a carrier who does not hover the tooltip, so the click says what is missing.
            onClick={canSubmitToDocumentReview ? toggleSubmitConfirmModal : toggleDocsRequiredModal}
            disabled={isMarkedCompleteState || isGlobalDisabled || isBusy}>
            <Icon
              path={mdiLock}
              size={1}
              className="mr-5 mobile:mr-1 shrink-0 mobile:!w-5 mobile:!h-5"
            />
            {submitLabel()}
          </Button>
        </Tooltip>
      </div>

      <ConfirmationModal
        title="Submit to Doc Review?"
        toggleConfirmModal={toggleSubmitConfirmModal}
        isConfirmModalOpen={isSubmitConfirmOpen}
        isLoading={isSubmittingToDocumentReview}
        content={
          <span className="block text-left">
            This moves the funding request to Document Review so our team can start processing it.
            Once submitted you will no longer be able to change the request or its documents. Press
            Save Updates first if you still have unsaved changes.
          </span>
        }
        labelOKButton="Submit to Doc Review"
        onOK={async () => {
          const didSubmit = await submitToDocumentReview();
          if (didSubmit) {
            toggleSubmitConfirmModal();
          }
        }}
      />

      <DocumentReviewRequirementModal
        isOpen={isDocsRequiredOpen}
        onClose={toggleDocsRequiredModal}
        attachments={attachments}
        isFuelAdvance={isFuelAdvance}
      />
    </>
  );
}
